import React from 'react'

import { ActionTypes, Config, SliderState } from './types'

const drawerShadow =
  'rgba(0, 0, 0, 0.16) 0 0.1875rem 0.375rem, rgba(0, 0, 0, 0.23) 0 0.1875rem 0.375rem'

type Action =
  | {
      type: ActionTypes.TOGGLE | ActionTypes.TOUCHEND
      payload?: never
    }
  | {
      type: ActionTypes.TOUCHMOVE | ActionTypes.TOUCHSTART
      payload: number
    }

const baseStyle: React.CSSProperties = {
  transitionTimingFunction: 'ease-in, ease-in',
  transitionProperty: 'transform, box-shadow',
  transitionDuration: '360ms, 0s',
  transitionDelay: '0s, 360ms',
  left: '100%',
  top: '0rem',
}

export const getInitialState = (
  defaultIsOpen = false,
  sliderWidth = 320
): SliderState => ({
  dragWidth: 0,
  isSliding: false,
  dragStarted: false,
  isOpen: defaultIsOpen,
  sliderStyle: {
    ...baseStyle,
    transform: defaultIsOpen
      ? `translateX(${-sliderWidth}px)`
      : 'translateX(0px)',
  },
})

export const getReducer =
  (config: Config) =>
  (state: SliderState, action: Action): SliderState => {
    const {
      sliderWidth = 320,
      touchTolerance = 8,
      toggleThreshold = 0.25 * window.innerWidth,
      widthDebounce = 2,
    } = config

    switch (action.type) {
      case ActionTypes.TOGGLE: {
        if (state.isSliding) {
          return state
        }
        const newOpen = !state.isOpen

        return {
          ...state,
          isOpen: newOpen,
          sliderStyle: {
            ...baseStyle,
            transform: `translateX(${newOpen ? -sliderWidth : 0}px)`,
            boxShadow: newOpen ? drawerShadow : 'none',
            transitionDelay: `0s, ${newOpen ? '0s' : '360ms'}`,
          },
        }
      }
      case ActionTypes.TOUCHSTART: {
        const startX = state.isOpen
          ? window.innerWidth - sliderWidth
          : window.innerWidth
        const shouldStart =
          action.payload >= startX - touchTolerance &&
          action.payload <= startX + touchTolerance

        if (state.dragStarted || !shouldStart) {
          return state
        }

        return {
          ...state,
          dragStarted: shouldStart,
          dragWidth: 0,
        }
      }
      case ActionTypes.TOUCHMOVE: {
        const initialTouch = state.isOpen
          ? window.innerWidth - sliderWidth
          : window.innerWidth
        const width = action.payload - initialTouch

        if (
          !state.dragStarted ||
          Math.abs(width - state.dragWidth) <= widthDebounce
        ) {
          return state
        }
        const transformX = state.isOpen ? width - sliderWidth : width

        return {
          ...state,
          dragWidth: width,
          isSliding: true,
          sliderStyle: {
            ...baseStyle,
            transform: `translateX(${
              transformX <= -sliderWidth ? -sliderWidth : transformX
            }px)`,
            transitionDuration: '0s, 0s',
            transitionDelay: '0s, 0s',
            boxShadow: drawerShadow,
          },
        }
      }
      case ActionTypes.TOUCHEND: {
        if (!state.isSliding) {
          return state
        }
        const shouldToggle = Math.abs(state.dragWidth) >= toggleThreshold
        const newOpen = shouldToggle ? !state.isOpen : state.isOpen

        const transitionDuration = newOpen
          ? (1 - Math.abs(state.dragWidth) / sliderWidth) * 360
          : (Math.abs(state.dragWidth) / sliderWidth) * 360

        return {
          ...state,
          isSliding: false,
          dragStarted: false,
          dragWidth: 0,
          isOpen: newOpen,
          sliderStyle: {
            ...baseStyle,
            transitionDuration: `${transitionDuration}ms, 0s`,
            transitionDelay: `0s, ${transitionDuration}ms`,
            transform: `translateX(${newOpen ? -sliderWidth : 0}px)`,
            boxShadow: newOpen ? drawerShadow : 'none',
          },
        }
      }
      default:
        return state
    }
  }
